import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus, AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader, Section, Field, Empty } from "../components/common";

const EMPTY = { item: "", category: "", quantity: 0, unit: "", min_quantity: 0, supplier: "", notes: "" };

export default function Inventory() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/inventory").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const low = list.filter((i) => Number(i.quantity) <= Number(i.min_quantity));
  const save = async (e) => {
    e.preventDefault();
    try { form.id ? await api.put(`/inventory/${form.id}`, form) : await api.post("/inventory", form); setForm(null); load(); toast.success(t("Saved", "Guardado")); } catch (err) { toast.error(errMsg(err)); }
  };
  const bump = async (i, d) => { await api.put(`/inventory/${i.id}`, { ...i, quantity: Math.max(0, Number(i.quantity) + d) }); load(); };
  const del = async (i) => { if (window.confirm(t("Delete item?", "¿Eliminar artículo?"))) { await api.delete(`/inventory/${i.id}`); load(); } };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  return (
    <>
      <PageHeader title={t("Inventory", "Inventario")} actions={<button className="btn-primary" onClick={() => setForm(EMPTY)} data-testid="inventory-new-btn"><Plus className="w-5 h-5" /> {t("Add item", "Agregar")}</button>} />
      {low.length > 0 && (
        <div className="card border-amber-200 bg-amber-50 p-4 mb-4 flex gap-3" data-testid="inventory-low-stock-alert">
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
          <div><div className="font-bold text-amber-800">{t("Low stock", "Inventario bajo")}: {low.length}</div><div className="text-sm text-amber-700">{low.map((i) => i.item).join(", ")}</div></div>
        </div>
      )}
      <Section testid="inventory-list">
        {!list.length && <Empty text={t("No items yet", "Sin artículos")} />}
        {list.map((i) => {
          const isLow = Number(i.quantity) <= Number(i.min_quantity);
          return (
            <div key={i.id} className="row-link" data-testid={`inventory-row-${i.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{i.item} {isLow && <span className="text-xs text-amber-700 bg-amber-100 rounded px-1.5 ml-1">{t("LOW", "BAJO")}</span>}</div>
                <div className="text-xs text-slate-500 truncate">{i.category} · {t("min", "mín")} {i.min_quantity} · {i.supplier}</div>
              </div>
              <button className="btn-outline btn-sm px-2" onClick={() => bump(i, -1)} data-testid={`inventory-minus-${i.id}`}><Minus className="w-4 h-4" /></button>
              <span className="mono font-semibold w-16 text-center">{i.quantity} <span className="text-xs text-slate-400">{i.unit}</span></span>
              <button className="btn-outline btn-sm px-2" onClick={() => bump(i, 1)} data-testid={`inventory-plus-${i.id}`}><Plus className="w-4 h-4" /></button>
              <button className="btn-ghost btn-sm px-2" onClick={() => setForm(i)} data-testid={`inventory-edit-${i.id}`}><Pencil className="w-4 h-4" /></button>
              <button className="btn-ghost btn-sm px-2 text-red-600" onClick={() => del(i)} data-testid={`inventory-delete-${i.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          );
        })}
      </Section>
      {form && (
        <Dialog open onOpenChange={(o) => !o && setForm(null)}>
          <DialogContent className="max-w-md" data-testid="inventory-dialog">
            <DialogHeader><DialogTitle>{form.id ? t("Edit item", "Editar artículo") : t("New item", "Nuevo artículo")}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="grid gap-3">
              <Field label={t("Item *", "Artículo *")}><input className="inp" required value={form.item} onChange={set("item")} data-testid="inventory-item-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("Category", "Categoría")}><input className="inp" value={form.category} onChange={set("category")} data-testid="inventory-category-input" /></Field>
                <Field label={t("Unit", "Unidad")}><input className="inp" value={form.unit} onChange={set("unit")} placeholder="bags, gal…" data-testid="inventory-unit-input" /></Field>
                <Field label={t("Quantity", "Cantidad")}><input className="inp" type="number" step="any" value={form.quantity} onChange={set("quantity")} data-testid="inventory-quantity-input" /></Field>
                <Field label={t("Minimum", "Mínimo")}><input className="inp" type="number" step="any" value={form.min_quantity} onChange={set("min_quantity")} data-testid="inventory-min-input" /></Field>
              </div>
              <Field label={t("Supplier", "Proveedor")}><input className="inp" value={form.supplier} onChange={set("supplier")} data-testid="inventory-supplier-input" /></Field>
              <Field label={t("Notes", "Notas")}><textarea className="inp" value={form.notes} onChange={set("notes")} data-testid="inventory-notes-input" /></Field>
              <button className="btn-primary" data-testid="inventory-save-btn">{t("Save", "Guardar")}</button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
